import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import "./App.css";

import { api, apercu, brancherDeconnexion, jeton } from "./api";
import { Shell } from "./components/Shell";
import { RdvFeuille } from "./components/RdvFeuille";
import { Squelette } from "./components/Bits";

import Landing from "./pages/Landing";
import Tarifs from "./pages/Tarifs";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Journee from "./pages/Journee";
import Agenda from "./pages/Agenda";
import Clients from "./pages/Clients";
import FicheClient from "./pages/FicheClient";
import Relances from "./pages/Relances";
import Finances from "./pages/Finances";
import Reglages from "./pages/Reglages";
import Reservation from "./pages/Reservation";
import Plus from "./pages/Plus";
import Confirmation from "./pages/Confirmation";
import EspaceClient from "./pages/EspaceClient";
import Logos from "./pages/Logos";
import Story from "./pages/Story";
import Assistant from "./pages/Assistant";
import Voix from "./pages/Voix";
import Marque from "./pages/Marque";
import Contenu from "./pages/Contenu";
import Admin from "./pages/Admin";
import { Aide, Conditions, Confidentialite, MentionsLegales } from "./pages/Legal";
import Styles from "./pages/Styles";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

function Chargement() {
  return (
    <div className="min-h-screen bg-[var(--paper)] pad pt-20">
      <div className="colonne">
        <Squelette lignes={3} />
      </div>
    </div>
  );
}

function Prive({ compte, children }) {
  if (!compte) return <Navigate to="/connexion" replace />;
  if (compte.role === "admin") return <Navigate to="/admin" replace />;
  if (!compte.onboarding_done) return <Navigate to="/bienvenue" replace />;
  return children;
}

/** Bandeau d'aperçu : l'admin voit l'écran du pro, sans jamais pouvoir y toucher. */
function BandeauApercu() {
  const nom = apercu.actif();
  if (!nom) return null;
  return (
    <div
      data-testid="bandeau-apercu"
      className="sticky top-0 left-0 right-0 z-[70] flex items-center justify-center gap-3 px-4 py-2"
      style={{ background: "var(--accent)", color: "#F7F7F5", fontSize: 13 }}
    >
      <span className="truncate">
        Aperçu de <strong>{nom}</strong> — lecture seule
      </span>
      <button
        onClick={() => {
          apercu.sortir();
          window.location.href = "/admin";
        }}
        data-testid="quitter-apercu"
        className="font-semibold underline shrink-0"
      >
        Quitter l’aperçu
      </button>
    </div>
  );
}

function Application() {
  const nav = useNavigate();
  const [compte, setCompte] = useState(null);
  const [pret, setPret] = useState(false);
  const [version, setVersion] = useState(0);
  const [rdvOuvert, setRdvOuvert] = useState(false);
  const [rdvContexte, setRdvContexte] = useState(null);

  const rafraichir = useCallback(() => setVersion((v) => v + 1), []);

  const deconnexion = useCallback(() => {
    jeton.effacer();
    setCompte(null);
    nav("/connexion", { replace: true });
  }, [nav]);

  useEffect(() => {
    brancherDeconnexion(() => {
      setCompte(null);
      nav("/connexion", { replace: true });
    });
  }, [nav]);

  useEffect(() => {
    let vivant = true;
    (async () => {
      if (!jeton.lire()) {
        setPret(true);
        return;
      }
      try {
        const { data } = await api.get("/auth/moi");
        if (vivant) setCompte(data);
      } catch {
        jeton.effacer();
      } finally {
        if (vivant) setPret(true);
      }
    })();
    return () => {
      vivant = false;
    };
  }, []);

  const ouvrirRdv = useCallback((contexte = null) => {
    setRdvContexte(contexte);
    setRdvOuvert(true);
  }, []);

  const valeur = useMemo(
    () => ({ compte, setCompte, deconnexion, rafraichir, version, ouvrirRdv }),
    [compte, deconnexion, rafraichir, version, ouvrirRdv]
  );

  if (!pret) return <Chargement />;

  const enveloppe = (page) => (
    <Prive compte={compte}>
      <Shell compte={compte} surDeconnexion={deconnexion} surNouveau={() => ouvrirRdv(null)}>
        {page}
      </Shell>
    </Prive>
  );

  return (
    <Ctx.Provider value={valeur}>
      <Helmet>
        <title>Servae — Chaque rendez-vous compte.</title>
      </Helmet>
      <BandeauApercu />
      <Routes>
        <Route
          path="/"
          element={
            compte?.role === "admin" ? (
              <Navigate to="/admin" replace />
            ) : compte?.onboarding_done ? (
              <Navigate to="/journee" replace />
            ) : (
              <Landing />
            )
          }
        />
        <Route
          path="/admin"
          element={
            compte?.role === "admin" ? (
              <Admin />
            ) : compte ? (
              <Navigate to="/journee" replace />
            ) : (
              <Navigate to="/connexion" replace />
            )
          }
        />

        <Route path="/tarifs" element={<Tarifs />} />
        <Route path="/styles" element={<Styles />} />
        <Route path="/connexion" element={<Auth mode="connexion" />} />
        <Route path="/inscription" element={<Auth mode="inscription" />} />
        <Route
          path="/bienvenue"
          element={compte ? <Onboarding /> : <Navigate to="/connexion" replace />}
        />
        <Route path="/journee" element={enveloppe(<Journee />)} />
        <Route path="/agenda" element={enveloppe(<Agenda />)} />
        <Route path="/clients" element={enveloppe(<Clients />)} />
        <Route path="/clients/:id" element={enveloppe(<FicheClient />)} />
        <Route path="/relances" element={enveloppe(<Relances />)} />
        <Route path="/finances" element={enveloppe(<Finances />)} />
        <Route path="/plus" element={enveloppe(<Plus compte={compte} surDeconnexion={deconnexion} />)} />
        <Route path="/reglages" element={enveloppe(<Reglages />)} />
        <Route path="/r/:slug" element={<Reservation />} />
        <Route path="/c/:token" element={<Confirmation />} />
        <Route path="/moi/:token" element={<EspaceClient />} />
        <Route path="/logo" element={<Logos />} />
        <Route path="/story" element={enveloppe(<Story />)} />
        <Route path="/assistant" element={enveloppe(<Assistant />)} />
        <Route path="/voix" element={enveloppe(<Voix />)} />
        <Route path="/marque" element={enveloppe(<Marque />)} />
        <Route path="/contenu" element={enveloppe(<Contenu />)} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/confidentialite" element={<Confidentialite />} />
        <Route path="/conditions" element={<Conditions />} />
        <Route path="/aide" element={<Aide />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {compte?.onboarding_done && (
        <RdvFeuille
          ouvert={rdvOuvert}
          contexte={rdvContexte}
          surFermeture={() => setRdvOuvert(false)}
          surEnregistrement={() => {
            setRdvOuvert(false);
            rafraichir();
          }}
        />
      )}
    </Ctx.Provider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Application />
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "var(--surface)",
            border: "1px solid var(--rule)",
            borderRadius: "4px",
            color: "var(--ink)",
            fontFamily: "var(--font-ui)",
            fontSize: "14px",
            boxShadow: "0 8px 20px rgba(23,19,16,0.07)",
          },
        }}
      />
    </BrowserRouter>
  );
}
